import { API } from '@/config';
import type {
  Issue,
  IssueListQuery,
  IssueListResponse,
  IssueReporter,
  RaiseIssueRequest,
  UpdateIssueRequest,
} from '@/types';
import { api } from './client';

/**
 * Support issues.
 *
 * A thin layer over the client, kept separate so components never build URLs or
 * know which endpoint does what.
 *
 * Note what is absent from every call: the organisation. The server takes it
 * from the token, so there is no parameter here that could name a different
 * lab - and nothing in the UI to get wrong.
 */
export const supportService = {
  list(query: IssueListQuery = {}, signal?: AbortSignal): Promise<IssueListResponse> {
    const params = new URLSearchParams();
    // Only what was asked for. Sending `status=undefined` would be a filter for
    // the literal string.
    if (query.status) params.set('status', query.status);
    if (query.category) params.set('category', query.category);
    if (query.limit !== undefined) params.set('limit', String(query.limit));
    if (query.offset !== undefined) params.set('offset', String(query.offset));

    const qs = params.toString();
    /*
     * The list is the one endpoint whose envelope carries `meta` beside `data`,
     * so it is fetched raw rather than through the unwrapping helper - the
     * total is what tells the page whether there is another page.
     */
    return api.getEnveloped<IssueListResponse>(
      `${API.endpoints.issues}${qs ? `?${qs}` : ''}`,
      signal ? { signal } : {},
    );
  },

  reporters(signal?: AbortSignal): Promise<IssueReporter[]> {
    return api.get<IssueReporter[]>(API.endpoints.issueReporters, signal ? { signal } : {});
  },

  raise(body: RaiseIssueRequest): Promise<Issue> {
    return api.post<Issue>(API.endpoints.issues, body);
  },

  update(issueId: string, body: UpdateIssueRequest): Promise<Issue> {
    return api.patch<Issue>(API.endpoints.issue(issueId), body);
  },
};
